import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';

/**
 * Iter132 — simplified one-step Forgot Password flow.
 *
 * The user types a username or email, taps "Email me a temp password",
 * and we're done.  Server:
 *   - generates a short temp password,
 *   - sets require_password_change on the user,
 *   - emails the temp password via Gmail SMTP (fire-and-forget).
 *
 * The user logs in with the temp password; Login.js detects the
 * require_password_change flag and routes them to /complete-setup to
 * pick a permanent password.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [masked, setMasked] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifier.trim()) { setError('Enter your username or email'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/forgot-password-simple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Request failed');
      setMasked(data?.email_masked || '');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <img src="/nexus-patch.png" alt="NexusTrac" className="w-44 h-44 md:w-56 md:h-56 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-heading font-bold text-white tracking-tight">PASSWORD RESET</h1>
          <p className="text-[10px] font-mono text-slate-400 uppercase tracking-[0.25em] mt-1">We'll email you a temp password</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-sm p-6">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm" data-testid="forgot-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {!done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <h2 className="text-sm font-heading font-semibold text-white mb-1">Reset Password</h2>
                <p className="text-xs text-slate-400 mb-4">
                  Enter your username or email. We'll instantly email you a one-time temporary password that forces a password change on your next sign-in.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400 uppercase tracking-wider font-mono">Username or Email</Label>
                <Input value={identifier} onChange={e => setIdentifier(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-sm"
                  placeholder="Your NexusTrac Email" required
                  data-testid="forgot-identifier-input" autoFocus />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-white text-[#0B192C] hover:bg-slate-200 font-heading font-semibold rounded-sm"
                data-testid="forgot-submit-btn">
                <Mail className="w-4 h-4 mr-2" />
                {loading ? 'Sending…' : 'Email me a temp password'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4 text-center" data-testid="forgot-done">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-emerald-500/15 border border-emerald-500/40 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <div>
                <h2 className="text-sm font-heading font-semibold text-white mb-1">Check your inbox</h2>
                <p className="text-xs text-slate-400">
                  {masked
                    ? <>We just emailed a temporary password to <span className="text-emerald-400 font-mono">{masked}</span>. Sign in with it and you'll be prompted to set a new password immediately.</>
                    : 'If an account matches, a temporary password has been emailed. Sign in with it and you\'ll be prompted to set a new password.'}
                </p>
                <p className="text-[11px] text-slate-500 font-mono mt-2 uppercase tracking-wider">Delivery usually arrives in &lt; 60 seconds</p>
              </div>
              <Button onClick={() => navigate('/login')}
                className="w-full bg-white text-[#0B192C] hover:bg-slate-200 font-heading font-semibold rounded-sm"
                data-testid="forgot-done-login-btn">
                Back to Sign In
              </Button>
            </div>
          )}
        </div>

        <div className="text-center">
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-300 font-mono uppercase tracking-wider">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
